import type { ReactNode } from "react";

type Block =
  | { kind: "h2"; text: string }
  | { kind: "h3"; text: string }
  | { kind: "p"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "blank" };

const INLINE = /(\*\*([^*]+)\*\*|(?<!\*)\*([^*]+)\*(?!\*)|`([^`]+)`)/g;

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const matches = [...text.matchAll(INLINE)];
  let cursor = 0;
  let n = 0;
  for (const m of matches) {
    const at = m.index ?? 0;
    if (at > cursor) out.push(text.slice(cursor, at));
    const [, , bold, italic, code] = m;
    if (bold !== undefined) out.push(<strong key={`${keyPrefix}-b-${n++}`}>{bold}</strong>);
    else if (italic !== undefined) out.push(<em key={`${keyPrefix}-i-${n++}`}>{italic}</em>);
    else if (code !== undefined)
      out.push(
        <code key={`${keyPrefix}-c-${n++}`} className="font-mono-badge text-xs">
          {code}
        </code>,
      );
    cursor = at + m[0].length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

function parse(md: string): Block[] {
  const lines = md.split("\n");
  const out: Block[] = [];
  let listBuf: string[] | null = null;

  function flushList() {
    if (listBuf) {
      out.push({ kind: "ul", items: listBuf });
      listBuf = null;
    }
  }

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) {
      flushList();
      out.push({ kind: "h2", text: line.slice(3) });
    } else if (line.startsWith("### ")) {
      flushList();
      out.push({ kind: "h3", text: line.slice(4) });
    } else if (/^\s*-\s+/.test(line)) {
      const item = line.replace(/^\s*-\s+/, "");
      if (listBuf) listBuf.push(item);
      else listBuf = [item];
    } else if (line.length === 0) {
      flushList();
      out.push({ kind: "blank" });
    } else {
      flushList();
      out.push({ kind: "p", text: line });
    }
  }
  flushList();
  return out;
}

/**
 * Tiny safe markdown renderer — emits real React nodes instead of HTML
 * strings. Supports the subset the LLM uses (## H2, ### H3, **bold**,
 * *italic*, backtick-code, - bullets, paragraphs). Untrusted content
 * cannot execute scripts because we never inject HTML.
 *
 * No panel container — the summary lives in flow as prose, not as a
 * widget. H2s drop a step (text-lg → was text-2xl) so they read as
 * intra-summary section breaks rather than fighting the page-level
 * verdict for visual weight.
 */
export function SummaryMd({ md, source }: { md: string; source: "llm" | "template" }) {
  const blocks = parse(md);
  return (
    <section aria-label="Toelichting" className="space-y-3">
      <p className="text-xs uppercase tracking-wider text-[var(--ink-faint)] font-mono-badge">
        Toelichting
      </p>
      {source === "template" ? (
        <p className="text-xs text-[var(--warn)]">
          ⚠ Korte samenvatting (de uitgebreide analyse is tijdelijk niet beschikbaar).
        </p>
      ) : null}
      <div className="space-y-1">
        {blocks.map((b, i) => {
          const k = `b-${i}`;
          switch (b.kind) {
            case "h2":
              return (
                <h2 key={k} className="text-lg font-semibold tracking-tight mt-5 mb-2">
                  {renderInline(b.text, k)}
                </h2>
              );
            case "h3":
              return (
                <h3
                  key={k}
                  className="text-base font-semibold mt-4 mb-1.5 text-[var(--ink-muted)]"
                >
                  {renderInline(b.text, k)}
                </h3>
              );
            case "p":
              return (
                <p key={k} className="leading-relaxed text-[var(--ink-muted)]">
                  {renderInline(b.text, k)}
                </p>
              );
            case "ul":
              return (
                <ul key={k} className="space-y-2 list-disc pl-5 text-[var(--ink-muted)]">
                  {b.items.map((it, j) => (
                    <li key={`${k}-li-${j}`}>{renderInline(it, `${k}-li-${j}`)}</li>
                  ))}
                </ul>
              );
            case "blank":
              return null;
          }
        })}
      </div>
    </section>
  );
}
